import { AcreageDrillDown } from '../response-models/acreage-drill-down.model';
import { AcreageOrganizationSummary } from '../response-models/acreage-organization-summary.model';
import { AcreagePdfExportResponse } from '../response-models/acreage-pdf-export-response.model';
import { AcreageReportFilterOptions } from '../response-models/acreage-report-filter-options.model';
import { AcreageReportOrgDetail } from '../response-models/acreage-report-org-detail.model';
import { AcreageReportSummary } from '../response-models/acreage-report-summary.model';
import { AcreageWeeklyDrillDown } from '../response-models/acreage-weekly-drill-down.model';
import { ChemicalAggregateRow } from '../response-models/chemical-aggregate-row.model';
import { ChemicalDrillDown } from '../response-models/chemical-drill-down.model';
import { ChemicalOption } from '../response-models/chemical-option.model';
import { ChemicalOrganizationSummary } from '../response-models/chemical-organization-summary.model';
import { ChemicalReportAggregate } from '../response-models/chemical-report-aggregate.model';
import { ChemicalReportFilterOptions } from '../response-models/chemical-report-filter-options.model';
import { ChemicalReportOrgDetail } from '../response-models/chemical-report-org-detail.model';
import { ChemicalReportSummary } from '../response-models/chemical-report-summary.model';
import { ChemicalUsageRow } from '../response-models/chemical-usage-row.model';
import { DateRangeHints } from '../response-models/date-range-hints.model';
import { OrganizationOption } from '../response-models/organization-option.model';
import { FaaReportOptions } from '../data-models/faa-report-options.model';
import { StateReportOptions } from '../data-models/state-report-options.model';
import { BatchMissingCountOrg } from '../response-models/batch-missing-count-org.model';
import { BatchMissingCountSummary } from '../response-models/batch-missing-count-summary.model';
import { FaaReportJob } from '../response-models/faa-report-job.model';
import { QueueStats } from '../response-models/queue-stats.model';
import { ReportBatchError } from '../response-models/report-batch-error.model';
import { ReportBatchResult } from '../response-models/report-batch-result.model';
import { isRecord, recordOrEmpty } from './is-record';
import { textOrNull } from './text-wire-utils';
import { countOrZero, idString, listOf, nullableDecimal, nullableNumber, nullableString } from './wire-value-utils';

/**
 * Readers for the admin report endpoints. Generated FAA and state reports (hub `admin-faa-reports-controller.ts` and
 * `admin-state-reports-controller.ts`) share their job, queue-stats and batch shapes. The acreage and chemical
 * reports (`admin-acreage-reports-controller.ts`, `admin-chemical-reports-controller.ts`) are read-only aggregates;
 * their totals arrive as numbers or Postgres numeric strings and read as 0 when missing (display only).
 */

/** The report's options, or `undefined` when the hub sent none. */
export function toFaaReportOptions(value: unknown): FaaReportOptions | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  return {
    includeFlightLogs: value['includeFlightLogs'] === true,
    includeSprayRecords: value['includeSprayRecords'] === true,
    includePilotInfo: value['includePilotInfo'] === true,
    includeAircraftInfo: value['includeAircraftInfo'] === true,
  };
}

/** The report's options, or `undefined` when the hub sent none. */
export function toStateReportOptions(value: unknown): StateReportOptions | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  return {
    includeChemicalDetails: value['includeChemicalDetails'] === true,
    includeApplicationRates: value['includeApplicationRates'] === true,
    includeFieldMaps: value['includeFieldMaps'] === true,
    includeWeatherConditions: value['includeWeatherConditions'] === true,
  };
}

/** One generation job of a report detail. */
export function toReportJob(value: unknown): FaaReportJob {
  const source = recordOrEmpty(value);
  return {
    id: idString(source['id']),
    name: nullableString(source['name']) ?? null,
    status: nullableString(source['status']) ?? null,
    progress: countOrZero(source['progress']),
    attemptsMade: countOrZero(source['attemptsMade']),
    createdAt: nullableNumber(source['createdAt']) ?? null,
    processedAt: nullableNumber(source['processedAt']) ?? null,
    finishedAt: nullableNumber(source['finishedAt']) ?? null,
    failedReason: nullableString(source['failedReason']) ?? null,
  };
}

/** Queue counts for the stats cards (display totals, so missing counts read as 0). */
export function toQueueStats(value: unknown): QueueStats {
  const source = recordOrEmpty(value);
  return {
    pending: countOrZero(source['pending']),
    active: countOrZero(source['active']),
    completed: countOrZero(source['completed']),
    failed: countOrZero(source['failed']),
  };
}

/** A batch generation result, building each created report with `toReport`. */
export function toReportBatchResult<T>(value: unknown, toReport: (item: unknown) => T): ReportBatchResult<T> {
  const source = recordOrEmpty(value);
  return {
    created: countOrZero(source['created']),
    reports: listOf(source['reports'], toReport),
    errors: listOf(source['errors'], toBatchError),
  };
}

/** The FAA missing-flight-count warning, or `null` when the hub sent none. */
export function toMissingCountSummary(value: unknown): BatchMissingCountSummary | null {
  if (!isRecord(value)) {
    return null;
  }
  return {
    organizationsChecked: countOrZero(value['organizationsChecked']),
    totalRecords: countOrZero(value['totalRecords']),
    recordsMissingCount: countOrZero(value['recordsMissingCount']),
    flightsMissingCount: countOrZero(value['flightsMissingCount']),
    organizations: listOf(value['organizations'], toMissingCountOrg),
  };
}

function toBatchError(value: unknown): ReportBatchError {
  const source = recordOrEmpty(value);
  return { organizationId: idString(source['organizationId']), error: nullableString(source['error']) ?? '' };
}

function toMissingCountOrg(value: unknown): BatchMissingCountOrg {
  const source = recordOrEmpty(value);
  return {
    organizationId: idString(source['organizationId']),
    recordsMissingCount: countOrZero(source['recordsMissingCount']),
    totalRecords: countOrZero(source['totalRecords']),
  };
}

// ---------- Acreage and chemical reports ----------

/** A report payload built by `read`, or `null` when the hub sent no object. */
export function reportOrNull<T>(data: unknown, read: (source: Record<string, unknown>) => T): T | null {
  return isRecord(data) ? read(data) : null;
}

/** A display total: a number or numeric string, else 0. */
function amount(value: unknown): number {
  return nullableDecimal(value) ?? 0;
}

function text(source: Record<string, unknown>, key: string): string {
  return nullableString(source[key]) ?? '';
}

function toOrganizationOption(value: unknown): OrganizationOption {
  const source = recordOrEmpty(value);
  return { id: idString(source['id']), name: text(source, 'name') };
}

function toDateRangeHints(value: unknown): DateRangeHints {
  const source = recordOrEmpty(value);
  return { minDate: textOrNull(source, 'minDate'), maxDate: textOrNull(source, 'maxDate') };
}

/** POST .../export-pdf: the pre-signed download link and its S3 key. */
export function toPdfExportResponse(source: Record<string, unknown>): { downloadUrl: string; s3Key: string } {
  return { downloadUrl: text(source, 'downloadUrl'), s3Key: text(source, 's3Key') };
}

function toAcreageDrillDown(value: unknown): AcreageDrillDown {
  const source = recordOrEmpty(value);
  return {
    id: idString(source['id']),
    name: text(source, 'name'),
    totalAcreage: amount(source['totalAcreage']),
    flightCount: countOrZero(source['flightCount']),
  };
}

function toAcreageWeek(value: unknown): AcreageWeeklyDrillDown {
  const source = recordOrEmpty(value);
  return {
    weekStartDate: text(source, 'weekStartDate'),
    weekEndDate: text(source, 'weekEndDate'),
    totalAcreage: amount(source['totalAcreage']),
    flightCount: countOrZero(source['flightCount']),
  };
}

export function toAcreageReportOrgDetail(source: Record<string, unknown>): AcreageReportOrgDetail {
  return {
    organizationId: idString(source['organizationId']),
    organizationName: text(source, 'organizationName'),
    startDate: text(source, 'startDate'),
    endDate: text(source, 'endDate'),
    totalAcreage: amount(source['totalAcreage']),
    totalFlights: countOrZero(source['totalFlights']),
    byDrone: listOf(source['byDrone'], toAcreageDrillDown),
    byPilot: listOf(source['byPilot'], toAcreageDrillDown),
    byField: listOf(source['byField'], toAcreageDrillDown),
    byWeek: listOf(source['byWeek'], toAcreageWeek),
  };
}

function toAcreageOrganization(value: unknown): AcreageOrganizationSummary {
  const source = recordOrEmpty(value);
  return {
    organizationId: idString(source['organizationId']),
    organizationName: text(source, 'organizationName'),
    totalAcreage: amount(source['totalAcreage']),
    flightCount: countOrZero(source['flightCount']),
  };
}

export function toAcreageReportSummary(source: Record<string, unknown>): AcreageReportSummary {
  return {
    startDate: text(source, 'startDate'),
    endDate: text(source, 'endDate'),
    organizations: listOf(source['organizations'], toAcreageOrganization),
    grandTotalAcreage: amount(source['grandTotalAcreage']),
    grandTotalFlights: countOrZero(source['grandTotalFlights']),
  };
}

export function toAcreageFilterOptions(source: Record<string, unknown>): AcreageReportFilterOptions {
  return {
    organizations: listOf(source['organizations'], toOrganizationOption),
    dateRangeHints: toDateRangeHints(source['dateRangeHints']),
  };
}

function toChemicalUsageRow(value: unknown): ChemicalUsageRow {
  const source = recordOrEmpty(value);
  return {
    chemicalName: text(source, 'chemicalName'),
    epaNumber: textOrNull(source, 'epaNumber'),
    totalAmountUsed: amount(source['totalAmountUsed']),
    unit: text(source, 'unit'),
    flightCount: countOrZero(source['flightCount']),
  };
}

function toChemicalDrillDown(value: unknown): ChemicalDrillDown {
  const source = recordOrEmpty(value);
  return {
    id: idString(source['id']),
    name: text(source, 'name'),
    chemicals: listOf(source['chemicals'], toChemicalUsageRow),
    totalFlights: countOrZero(source['totalFlights']),
  };
}

export function toChemicalReportOrgDetail(source: Record<string, unknown>): ChemicalReportOrgDetail {
  return {
    organizationId: idString(source['organizationId']),
    organizationName: text(source, 'organizationName'),
    startDate: text(source, 'startDate'),
    endDate: text(source, 'endDate'),
    chemicalUsage: listOf(source['chemicalUsage'], toChemicalUsageRow),
    byField: listOf(source['byField'], toChemicalDrillDown),
    byPilot: listOf(source['byPilot'], toChemicalDrillDown),
    byDrone: listOf(source['byDrone'], toChemicalDrillDown),
  };
}

function toChemicalOrganization(value: unknown): ChemicalOrganizationSummary {
  const source = recordOrEmpty(value);
  return {
    organizationId: idString(source['organizationId']),
    organizationName: text(source, 'organizationName'),
    chemicals: listOf(source['chemicals'], toChemicalUsageRow),
    totalFlights: countOrZero(source['totalFlights']),
  };
}

export function toChemicalReportSummary(source: Record<string, unknown>): ChemicalReportSummary {
  return {
    startDate: text(source, 'startDate'),
    endDate: text(source, 'endDate'),
    manufacturer: textOrNull(source, 'manufacturer'),
    organizations: listOf(source['organizations'], toChemicalOrganization),
    grandTotalFlights: countOrZero(source['grandTotalFlights']),
  };
}

function toChemicalAggregateRow(value: unknown): ChemicalAggregateRow {
  const source = recordOrEmpty(value);
  return {
    chemicalName: text(source, 'chemicalName'),
    manufacturer: textOrNull(source, 'manufacturer'),
    epaNumber: textOrNull(source, 'epaNumber'),
    totalAmountUsed: amount(source['totalAmountUsed']),
    unit: text(source, 'unit'),
    flightCount: countOrZero(source['flightCount']),
    organizationCount: countOrZero(source['organizationCount']),
  };
}

export function toChemicalReportAggregate(source: Record<string, unknown>): ChemicalReportAggregate {
  return {
    startDate: text(source, 'startDate'),
    endDate: text(source, 'endDate'),
    manufacturer: textOrNull(source, 'manufacturer'),
    chemicals: listOf(source['chemicals'], toChemicalAggregateRow),
    grandTotalFlights: countOrZero(source['grandTotalFlights']),
  };
}

function toChemicalOption(value: unknown): ChemicalOption {
  const source = recordOrEmpty(value);
  return {
    id: idString(source['id']),
    name: text(source, 'name'),
    epaNumber: textOrNull(source, 'epaNumber'),
    manufacturer: textOrNull(source, 'manufacturer'),
  };
}

export function toChemicalFilterOptions(source: Record<string, unknown>): ChemicalReportFilterOptions {
  return {
    organizations: listOf(source['organizations'], toOrganizationOption),
    manufacturers: listOf(source['manufacturers'], item => (typeof item === 'string' ? item : '')).filter(Boolean),
    chemicals: listOf(source['chemicals'], toChemicalOption),
    dateRangeHints: toDateRangeHints(source['dateRangeHints']),
  };
}
